import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { CheckCircle2, XCircle, ExternalLink, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

/**
 * Tradier broker connection card.
 *
 * Fastest same-day real-broker path — instant sandbox token from dash.tradier.com.
 * Paper-trading data comes back exactly like production, just with delayed quotes.
 */
export default function TradierCard() {
  const [status, setStatus] = useState({ connected: false, env: null, account_id: null, token_masked: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [token, setToken] = useState("");
  const [env, setEnv] = useState("paper");

  const load = () =>
    api.get("/broker/tradier/settings")
      .then((r) => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (token.trim().length < 10) { toast.error("Token looks too short."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/broker/tradier/settings", {
        token: token.trim(), env,
      });
      toast.success(`Connected to Tradier ${data.env} · account ${data.account_id}`);
      setToken(""); setShowForm(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tradier rejected the token.");
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Tradier?")) return;
    try {
      await api.delete("/broker/tradier/settings");
      toast.success("Tradier disconnected");
      load();
    } catch { toast.error("Disconnect failed"); }
  };

  return (
    <Card className="p-6 border-border rounded-xl" data-testid="tradier-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#00B8F0] text-white flex items-center justify-center font-display font-bold">T</div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-semibold">Tradier</div>
              {status.connected && (
                <Badge variant="secondary" data-testid="tradier-connected-badge" className="rounded-full">
                  <CheckCircle2 className="w-3 h-3 mr-1 text-[hsl(var(--gain))]" />
                  Connected · {status.env} · {status.account_id}
                </Badge>
              )}
              <Badge className="bg-[hsl(var(--gain))]/15 text-[hsl(var(--gain))] border-[hsl(var(--gain))]/30" variant="outline">
                <Zap className="w-3 h-3 mr-1" /> Instant sandbox token
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-lg">
              Fastest way to real broker data today — Tradier gives you a sandbox token instantly
              after account creation. Real quotes (delayed), real balances, real order fills.
            </p>
            {status.connected && (
              <div className="mt-3 text-xs text-muted-foreground font-mono-tab" data-testid="tradier-masked-token">
                Token: {status.token_masked || "—"} · updated {status.updated_at ? new Date(status.updated_at).toLocaleString() : "—"}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {status.connected ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowForm(!showForm)} className="rounded-full" data-testid="tradier-reconfigure">
                Reconfigure
              </Button>
              <Button variant="ghost" size="sm" onClick={disconnect} className="rounded-full text-[hsl(var(--loss))]" data-testid="tradier-disconnect">
                Disconnect
              </Button>
            </>
          ) : !loading && (
            <Button size="sm" onClick={() => setShowForm(true)} className="rounded-full" data-testid="tradier-open-form">
              <Zap className="w-4 h-4 mr-1" /> Connect Tradier
            </Button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="mt-5 pt-5 border-t border-border space-y-4" data-testid="tradier-form">
          <div className="text-xs bg-secondary/40 border border-border rounded-md p-3 space-y-1">
            <div className="text-foreground font-medium">Get your Tradier sandbox token (free, ~5 minutes):</div>
            <ol className="list-decimal list-inside space-y-0.5 text-muted-foreground">
              <li>Create a Tradier account at <a className="underline text-[hsl(var(--ai))]" href="https://developer.tradier.com/user/sign_up" target="_blank" rel="noreferrer">developer.tradier.com <ExternalLink className="w-2.5 h-2.5 inline" /></a>.</li>
              <li>Confirm your email.</li>
              <li>Go to <a className="underline text-[hsl(var(--ai))]" href="https://dash.tradier.com/settings/api" target="_blank" rel="noreferrer">API Settings <ExternalLink className="w-2.5 h-2.5 inline" /></a>.</li>
              <li>Copy the <b>Sandbox Token</b> (long string). Paper environment gives ~$100k virtual balance.</li>
              <li>Paste it here ↓</li>
            </ol>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground mb-1">Environment</Label>
            <div className="flex gap-2">
              {["paper", "live"].map((v) => (
                <button
                  key={v}
                  onClick={() => setEnv(v)}
                  data-testid={`tradier-env-${v}`}
                  className={`text-xs uppercase tracking-wider rounded-full px-3 py-1.5 border ${env === v
                    ? "bg-[hsl(var(--ai))]/15 border-[hsl(var(--ai))] text-[hsl(var(--ai))]"
                    : "border-border text-muted-foreground"}`}
                >
                  {v === "paper" ? "Paper (Sandbox)" : "Live"}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground mb-1">API Token</Label>
            <Input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Long sandbox token"
              className="font-mono-tab text-xs"
              data-testid="tradier-token-input"
              autoComplete="off"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Encrypted at rest. Never sent to the browser after save.
            </div>
          </div>

          {env === "live" && (
            <div className="text-xs text-[hsl(var(--loss))] bg-[hsl(var(--loss))]/10 border border-[hsl(var(--loss))]/30 rounded p-2 flex gap-2">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>You're saving a <b>LIVE</b> Tradier token — real money will be at risk.</div>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="tradier-save">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save & verify
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
